import { useField } from 'react-final-form';

import { Button, Icons, Typography, Stack, tokens } from '@cast/design-system';

import { PolicyInput } from '../PolicyInput';
import { useNullableCard } from './_hooks/useNullableCard';

import { useControlsGroupState } from '@cast/shared-rff-ui';

const DEFAULT_UTILIZATION = 90;

type MetricCardProps = {
  label: string;
  name: 'cpu' | 'memory';
  addButtonText: string;
};

const MetricCard = ({ label, name, addButtonText }: MetricCardProps) => {
  const { readOnly, name: fieldName = '' } = useControlsGroupState({ name });
  const { input } = useField<number | null>(fieldName, { allowNull: true });
  const { isAdded, handleAdd, handleRemove } = useNullableCard({
    input,
    defaultValue: DEFAULT_UTILIZATION,
  });

  if (!isAdded) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        sx={{
          flex: '0 0 calc(50% - 4px)',
          p: 12,
          py: 35,
          borderRadius: '4px',
          backgroundColor: tokens.theme.bg.structural.surface,
          border: '1px solid',
          borderColor: tokens.theme.border.structural.neutralSubtle,
          minHeight: 78,
        }}
      >
        <Button
          variant="text"
          size="md"
          startIcon={<Icons.Plus />}
          disabled={readOnly}
          onClick={handleAdd}
          data-testid={`add-${name}-utilization`}
        >
          {addButtonText}
        </Button>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      gap={8}
      sx={{
        flex: '0 0 calc(50% - 4px)',
        pt: 16,
        pb: 24,
        px: 12,
        borderRadius: '4px',
        border: '1px solid',
        borderColor: tokens.theme.border.structural.neutral,
      }}
    >
      <Stack direction="column" gap={4} sx={{ flex: 1 }}>
        <Typography
          variant="P10M"
          noWrap
          color={readOnly ? tokens.theme.fg.structural.secondary : undefined}
        >
          {label}
        </Typography>
        <PolicyInput
          name={name}
          variant="percentage"
          max={10000}
          sx={{ width: '100%' }}
          testId={`${name}-utilization`}
        />
      </Stack>
      <Button
        variant="ghost"
        size="sm"
        disabled={readOnly}
        onClick={handleRemove}
        sx={{ minWidth: 0, p: 4, alignSelf: 'flex-end' }}
        data-testid={`remove-${name}-utilization`}
      >
        <Icons.X size={16} />
      </Button>
    </Stack>
  );
};

export const TriggersField = () => {
  return (
    <Stack direction="row" gap={8} sx={{ width: '100%' }}>
      <MetricCard
        label="Avg. CPU utilization"
        name="cpu"
        addButtonText="Add CPU utilization"
      />
      <MetricCard
        label="Avg. Memory utilization"
        name="memory"
        addButtonText="Add Memory utilization"
      />
    </Stack>
  );
};
